import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Heart, Briefcase, Sparkles, DollarSign, Users, GraduationCap, 
  Compass, Plane, ChevronRight, ChevronLeft, Calendar, TrendingUp,
  Activity, Shield, Star, Sun, Moon, Zap
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// ============================================================================
// DESIGN TOKENS - Dark Glassmorphism Theme
// ============================================================================
const theme = {
  bg: {
    primary: '#050508',
    card: 'rgba(255, 255, 255, 0.03)',
    cardHover: 'rgba(255, 255, 255, 0.06)',
    glass: 'rgba(255, 255, 255, 0.05)',
  },
  border: {
    subtle: 'rgba(255, 255, 255, 0.06)',
    glow: 'rgba(255, 255, 255, 0.1)',
  },
  text: {
    primary: '#FFFFFF',
    secondary: 'rgba(255, 255, 255, 0.6)',
    muted: 'rgba(255, 255, 255, 0.4)',
  }
};

// Domain colors
const domainColors = {
  health: { gradient: 'from-emerald-500 to-teal-400', bg: 'rgba(16, 185, 129, 0.1)', text: '#10b981' },
  career: { gradient: 'from-amber-500 to-orange-400', bg: 'rgba(245, 158, 11, 0.1)', text: '#f59e0b' },
  love: { gradient: 'from-pink-500 to-rose-400', bg: 'rgba(236, 72, 153, 0.1)', text: '#ec4899' },
  wealth: { gradient: 'from-green-500 to-emerald-400', bg: 'rgba(34, 197, 94, 0.1)', text: '#22c55e' },
  family: { gradient: 'from-sky-500 to-blue-400', bg: 'rgba(14, 165, 233, 0.1)', text: '#0ea5e9' },
  education: { gradient: 'from-violet-500 to-purple-400', bg: 'rgba(139, 92, 246, 0.1)', text: '#8b5cf6' },
  spirit: { gradient: 'from-amber-400 to-yellow-300', bg: 'rgba(251, 191, 36, 0.1)', text: '#fbbf24' },
  travel: { gradient: 'from-cyan-500 to-teal-400', bg: 'rgba(6, 182, 212, 0.1)', text: '#06b6d4' },
};

// ============================================================================
// COMPONENTS
// ============================================================================

// Animated Background
const Background = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute inset-0 bg-[#050508]" />
    <div className="absolute top-0 left-0 w-full h-full">
      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-emerald-500/5 blur-[120px]" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full bg-violet-500/5 blur-[120px]" />
      <div className="absolute top-[40%] left-[50%] w-[400px] h-[400px] rounded-full bg-blue-500/3 blur-[100px]" />
    </div>
    {/* Removed grid pattern - was causing visible lines */}
  </div>
);

// Score Ring Component
const ScoreRing = ({ score, size = 120, color = 'emerald' }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  
  const gradients = {
    emerald: ['#10b981', '#14b8a6'],
    amber: ['#f59e0b', '#fb923c'],
    pink: ['#ec4899', '#f472b6'],
    violet: ['#8b5cf6', '#a78bfa'],
  };
  
  const [start, end] = gradients[color] || gradients.emerald;
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="w-full h-full -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="6"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#gradient-${color})`}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
        <defs>
          <linearGradient id={`gradient-${color}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={start} />
            <stop offset="100%" stopColor={end} />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-white">{score}</span>
        <span className="text-xs text-white/40">score</span>
      </div>
    </div>
  );
};

// Domain Card Component
const DomainCard = ({ domain, isExpanded, onClick }) => {
  const colors = domainColors[domain.key] || domainColors.health;
  
  return (
    <motion.div
      layout
      onClick={onClick}
      className={`relative cursor-pointer rounded-2xl border transition-all duration-300 overflow-hidden
        ${isExpanded ? 'col-span-2 row-span-2' : ''}
        bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.04]
      `}
      whileHover={{ scale: isExpanded ? 1 : 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Glow effect */}
      <div className={`absolute inset-0 opacity-0 hover:opacity-100 transition-opacity bg-gradient-to-br ${colors.gradient} blur-3xl`} style={{ opacity: 0.05 }} />
      
      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${colors.gradient}`}>
              {domain.icon}
            </div>
            <div>
              <h3 className="text-white font-medium">{domain.name}</h3>
              <p className="text-white/40 text-xs">{domain.subtitle}</p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-white">{domain.score}</span>
            <div className={`text-xs ${domain.trend > 0 ? 'text-emerald-400' : domain.trend < 0 ? 'text-rose-400' : 'text-white/40'}`}>
              {domain.trend > 0 ? '+' : ''}{domain.trend}%
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden mb-3">
          <motion.div
            className={`h-full bg-gradient-to-r ${colors.gradient} rounded-full`}
            initial={{ width: 0 }}
            animate={{ width: `${domain.score}%` }}
            transition={{ duration: 1, delay: 0.2 }}
          />
        </div>
        
        {/* Focus */}
        <p className="text-white/50 text-sm line-clamp-1">{domain.focus}</p>
        
        {/* Expanded content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-white/[0.06] space-y-3"
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/[0.03] rounded-xl p-3">
                  <p className="text-white/40 text-xs mb-1">Strengths</p>
                  <p className="text-white text-sm">{domain.strengths?.[0] || 'Good foundation'}</p>
                </div>
                <div className="bg-white/[0.03] rounded-xl p-3">
                  <p className="text-white/40 text-xs mb-1">Focus Area</p>
                  <p className="text-white text-sm">{domain.watchAreas?.[0] || 'Maintain balance'}</p>
                </div>
              </div>
              <p className="text-white/60 text-sm">{domain.insight}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Monthly Calendar Component
const MonthlyCalendar = ({ year, month, data }) => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  
  const getDaysInMonth = () => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };
  
  const { firstDay, daysInMonth } = getDaysInMonth();
  
  const getDayStatus = (day) => {
    // Real data would come from API
    const hash = (day * 7 + month * 13) % 5;
    const statuses = ['excellent', 'good', 'good', 'moderate', 'careful'];
    return statuses[hash];
  };
  
  const statusColors = {
    excellent: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    good: 'bg-green-500/20 text-green-400 border-green-500/30',
    moderate: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    careful: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  };
  
  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
      <h3 className="text-xl font-semibold text-white mb-6">{monthNames[month]} {year}</h3>
      
      {/* Week days */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div key={i} className="text-center text-white/30 text-xs font-medium py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Days grid */}
      <div className="grid grid-cols-7 gap-2">
        {/* Empty cells for first week */}
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" />
        ))}
        
        {/* Days */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const status = getDayStatus(day);
          const isToday = day === new Date().getDate() && month === new Date().getMonth();
          
          return (
            <motion.div
              key={day}
              whileHover={{ scale: 1.1 }}
              className={`aspect-square rounded-lg flex items-center justify-center text-sm font-medium cursor-pointer
                border transition-all
                ${isToday ? 'ring-2 ring-white/30' : ''}
                ${statusColors[status]}
              `}
            >
              {day}
            </motion.div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex justify-center gap-4 mt-6 pt-4 border-t border-white/[0.06]">
        {[
          { status: 'excellent', label: 'Excellent' },
          { status: 'good', label: 'Good' },
          { status: 'moderate', label: 'Moderate' },
          { status: 'careful', label: 'Careful' },
        ].map(({ status, label }) => (
          <div key={status} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded ${statusColors[status].split(' ')[0]}`} />
            <span className="text-xs text-white/40">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Yearly Timeline Component
const YearlyTimeline = ({ year, predictions }) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const getMonthData = (monthIndex) => {
    // From real API data or generated
    const hash = (monthIndex * 17 + year) % 4;
    const levels = ['excellent', 'good', 'moderate', 'careful'];
    const focuses = ['Health', 'Career', 'Love', 'Wealth', 'Family', 'Learning', 'Spirit', 'Travel'];
    
    return {
      level: levels[hash],
      focus: focuses[monthIndex % 8],
      score: 70 + (hash * 8),
    };
  };
  
  const levelColors = {
    excellent: 'from-emerald-500 to-teal-400',
    good: 'from-green-500 to-emerald-400',
    moderate: 'from-yellow-500 to-amber-400',
    careful: 'from-orange-500 to-red-400',
  };
  
  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6">
      <h3 className="text-xl font-semibold text-white mb-6">{year} Overview</h3>
      
      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-3">
        {months.map((month, index) => {
          const data = getMonthData(index);
          const isCurrentMonth = index === new Date().getMonth() && year === new Date().getFullYear();
          
          return (
            <motion.div
              key={month}
              whileHover={{ scale: 1.05, y: -5 }}
              className={`relative rounded-xl p-3 cursor-pointer transition-all
                bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.15]
                ${isCurrentMonth ? 'ring-2 ring-emerald-500/50' : ''}
              `}
            >
              {/* Top gradient bar */}
              <div className={`absolute top-0 left-0 right-0 h-1 rounded-t-xl bg-gradient-to-r ${levelColors[data.level]}`} />
              
              <div className="pt-2 text-center">
                <p className="text-white/40 text-xs mb-2">{month}</p>
                <p className="text-white font-semibold text-lg">{data.score}</p>
                <p className="text-white/30 text-[10px] mt-1">{data.focus}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const UltimateDashboard = () => {
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, monthly, yearly
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Get birth data
  const getBirthData = () => {
    try {
      const stored = localStorage.getItem('birthData');
      if (stored) {
        const parsed = JSON.parse(stored);
        const dateParts = parsed.date?.split('-') || ['1997', '05', '14'];
        const timeParts = parsed.time?.split(':') || ['12', '00'];
        return {
          name: parsed.full_name || 'User',
          year: parseInt(dateParts[0]),
          month: parseInt(dateParts[1]),
          day: parseInt(dateParts[2]),
          hour: parseInt(timeParts[0]),
          minute: parseInt(timeParts[1]),
          latitude: parsed.latitude || 28.6139,
          longitude: parsed.longitude || 77.2090,
          gender: parsed.gender?.toLowerCase() || 'unknown'
        };
      }
    } catch (e) { }
    return { name: 'User', year: 1997, month: 5, day: 14, hour: 12, minute: 0, latitude: 30.32, longitude: 78.03, gender: 'male' };
  };

  // Fetch real health data
  useEffect(() => {
    const fetchHealth = async () => {
      setLoading(true);
      try {
        const birthData = getBirthData();
        const [dashRes, futureRes] = await Promise.all([
          axios.post(`${BACKEND_URL}/api/health/dashboard-card`, birthData),
          axios.post(`${BACKEND_URL}/api/health/future-predictions`, birthData)
        ]);
        setHealthData({ dashboard: dashRes.data, future: futureRes.data });
      } catch (e) {
        console.error('Error fetching health:', e);
      }
      setLoading(false);
    };
    fetchHealth();
  }, []);

  // Calculate health score from real data
  const getHealthScore = () => {
    if (!healthData?.dashboard?.body_systems) return 85;
    const scores = healthData.dashboard.body_systems.map(s => {
      switch(s.status) {
        case 'strong': return 95;
        case 'balanced': return 75;
        case 'sensitive': return 55;
        default: return 65;
      }
    });
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  // Domains data
  const domains = [
    { key: 'health', name: 'Health', subtitle: 'Longevity & Wellness', icon: <Heart className="w-5 h-5 text-white" />, score: getHealthScore(), trend: 5, focus: healthData?.dashboard?.focus_area || 'General wellness', strengths: healthData?.dashboard?.strong_areas, watchAreas: healthData?.dashboard?.sensitive_areas, insight: healthData?.dashboard?.constitution },
    { key: 'career', name: 'Career', subtitle: 'Success & Growth', icon: <Briefcase className="w-5 h-5 text-white" />, score: 88, trend: 12, focus: 'Leadership opportunities', strengths: ['Strategic thinking'], watchAreas: ['Work-life balance'], insight: 'Strong professional momentum' },
    { key: 'love', name: 'Love', subtitle: 'Relationships', icon: <Sparkles className="w-5 h-5 text-white" />, score: 76, trend: -3, focus: 'Communication', strengths: ['Emotional depth'], watchAreas: ['Patience'], insight: 'Focus on understanding' },
    { key: 'wealth', name: 'Wealth', subtitle: 'Finance & Prosperity', icon: <DollarSign className="w-5 h-5 text-white" />, score: 82, trend: 8, focus: 'Investment growth', strengths: ['Financial discipline'], watchAreas: ['Risk assessment'], insight: 'Steady accumulation phase' },
    { key: 'family', name: 'Family', subtitle: 'Home & Harmony', icon: <Users className="w-5 h-5 text-white" />, score: 91, trend: 4, focus: 'Quality time', strengths: ['Strong bonds'], watchAreas: ['Patience'], insight: 'Harmonious period' },
    { key: 'education', name: 'Learning', subtitle: 'Skills & Knowledge', icon: <GraduationCap className="w-5 h-5 text-white" />, score: 79, trend: 15, focus: 'New skills', strengths: ['Quick learning'], watchAreas: ['Focus'], insight: 'Growth mindset active' },
    { key: 'spirit', name: 'Spirit', subtitle: 'Inner Growth', icon: <Sun className="w-5 h-5 text-white" />, score: 85, trend: 7, focus: 'Mindfulness', strengths: ['Intuition'], watchAreas: ['Consistency'], insight: 'Spiritual awakening' },
    { key: 'travel', name: 'Travel', subtitle: 'Adventure', icon: <Plane className="w-5 h-5 text-white" />, score: 73, trend: 0, focus: 'Planning', strengths: ['Adaptability'], watchAreas: ['Timing'], insight: 'Favorable for short trips' },
  ];

  const overallScore = Math.round(domains.reduce((sum, d) => sum + d.score, 0) / domains.length);

  return (
    <div className="min-h-screen text-white pb-24">
      <Background />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Life Dashboard</h1>
            <p className="text-white/40 text-sm">Your cosmic intelligence</p>
          </div>
          <ScoreRing score={overallScore} size={80} />
        </div>
        
        {/* Navigation */}
        <div className="flex gap-2 mb-6 p-1 bg-white/[0.03] rounded-xl w-fit">
          {[
            { id: 'dashboard', label: 'Overview', icon: <Activity className="w-4 h-4" /> },
            { id: 'monthly', label: 'Monthly', icon: <Calendar className="w-4 h-4" /> },
            { id: 'yearly', label: 'Yearly', icon: <TrendingUp className="w-4 h-4" /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${activeView === tab.id 
                  ? 'bg-white/10 text-white' 
                  : 'text-white/40 hover:text-white/60'
                }
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Content */}
        <AnimatePresence mode="wait">
          {activeView === 'dashboard' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {domains.map((domain) => (
                <DomainCard
                  key={domain.key}
                  domain={domain}
                  isExpanded={expandedDomain === domain.key}
                  onClick={() => setExpandedDomain(expandedDomain === domain.key ? null : domain.key)}
                />
              ))}
            </motion.div>
          )}
          
          {activeView === 'monthly' && (
            <motion.div
              key="monthly"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setSelectedMonth(m => m === 0 ? 11 : m - 1)}
                  className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-all"
                >
                  <ChevronLeft className="w-5 h-5 text-white/60" />
                </button>
                <button
                  onClick={() => setSelectedMonth(m => m === 11 ? 0 : m + 1)}
                  className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-all"
                >
                  <ChevronRight className="w-5 h-5 text-white/60" />
                </button>
              </div>
              
              <MonthlyCalendar year={selectedYear} month={selectedMonth} />
            </motion.div>
          )}
          
          {activeView === 'yearly' && (
            <motion.div
              key="yearly"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* Year navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setSelectedYear(y => y - 1)}
                  className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-all"
                >
                  <ChevronLeft className="w-5 h-5 text-white/60" />
                </button>
                <span className="text-white font-semibold">{selectedYear}</span>
                <button
                  onClick={() => setSelectedYear(y => y + 1)}
                  className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] transition-all"
                >
                  <ChevronRight className="w-5 h-5 text-white/60" />
                </button>
              </div>
              
              <YearlyTimeline year={selectedYear} predictions={healthData?.future?.predictions} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Bottom Navigation */}
      <div data-kb-hide className="fixed bottom-0 left-0 right-0 bg-[#0a0a0f]/90 backdrop-blur-xl border-t border-white/[0.06]">
        <div className="max-w-lg mx-auto flex justify-around py-3">
          {[
            { icon: <Sparkles className="w-5 h-5" />, label: 'Story', active: false },
            { icon: <Activity className="w-5 h-5" />, label: 'Dashboard', active: true },
            { icon: <Calendar className="w-5 h-5" />, label: 'Timeline', active: false },
            { icon: <Users className="w-5 h-5" />, label: 'Profile', active: false },
          ].map((item, i) => (
            <button
              key={i}
              className={`flex flex-col items-center gap-1 px-4 py-1 rounded-lg transition-all
                ${item.active ? 'text-emerald-400' : 'text-white/40 hover:text-white/60'}
              `}
            >
              {item.icon}
              <span className="text-[10px]">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UltimateDashboard;
